import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type ChatContactType = 'friend' | 'organizer' | 'other';

export interface Chat {
  id: string;
  user1_id: string;
  user2_id: string;
  last_message?: string;
  last_message_at?: string;
  created_at: string;
  other_user?: {
    id: string;
    full_name: string;
    avatar_url?: string;
  };
  contact_type?: ChatContactType;
  unread_count?: number;
}

export interface ChatMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  message: string;
  created_at: string;
  sender?: {
    full_name: string;
    avatar_url?: string;
  };
}

export function useChats() {
  const [chats, setChats] = useState<Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const hasFetchedRef = useRef(false);
  const channelId = useRef(Math.random().toString(36).slice(2));

  const fetchChats = async () => {
    try {
      if (!hasFetchedRef.current) setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data, error } = await supabase
        .from('chats')
        .select(`
          id,
          user1_id,
          user2_id,
          last_message,
          last_message_at,
          created_at
        `)
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .not('last_message', 'is', null)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (error) throw error;
      
      // Get other user profiles in a separate query
      const chatsWithOtherUser = [];
      for (const chat of data || []) {
        const isUser1 = chat.user1_id === user.id;
        const otherUserId = isUser1 ? chat.user2_id : chat.user1_id;
        
        // Try to get other user profile
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('user_id', otherUserId)
          .single();
        
        chatsWithOtherUser.push({
          ...chat,
          other_user: {
            id: otherUserId,
            full_name: profileData?.full_name || 'Usuario',
            avatar_url: profileData?.avatar_url
          }
        });
      }
      
      setChats(chatsWithOtherUser);
      
      // Get unread counts per chat
      const chatIds = chatsWithOtherUser.map(c => c.id);
      if (chatIds.length > 0) {
        const { data: unreadData } = await supabase
          .from('chat_messages')
          .select('chat_id')
          .in('chat_id', chatIds)
          .neq('sender_id', user.id)
          .is('read_at', null);

        const unreadCounts: Record<string, number> = {};
        let totalUnread = 0;
        (unreadData || []).forEach(msg => {
          unreadCounts[msg.chat_id] = (unreadCounts[msg.chat_id] || 0) + 1;
          totalUnread++;
        });

        // Assign unread_count to each chat
        chatsWithOtherUser.forEach(chat => {
          chat.unread_count = unreadCounts[chat.id] || 0;
        });

        setUnreadCount(totalUnread);
      } else {
        setUnreadCount(0);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      hasFetchedRef.current = true;
    }
  };

  const deleteChat = async (chatId: string) => {
    try {
      const { error } = await supabase
        .from('chats')
        .delete()
        .eq('id', chatId);

      if (error) throw error;
      
      setChats(prev => prev.filter(c => c.id !== chatId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Error deleting chat:', err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchChats();

    // Set up real-time subscription
    const channel = supabase
      .channel(`chats-changes-${channelId.current}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'chats' }, 
        () => {
          fetchChats();
        }
      )
      .subscribe();

    const messagesChannel = supabase
      .channel(`chat-messages-changes-${channelId.current}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'chat_messages' }, 
        () => {
          fetchChats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(messagesChannel);
    };
  }, []);

  return { 
    chats, 
    loading, 
    error, 
    unreadCount,
    deleteChat,
    refetch: fetchChats 
  };
}

export function useChatMessages(chatId: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const fetchMessages = async () => {
    if (!chatId) return;
    
    try {
      if (!hasFetchedRef.current) {
        setLoading(true);
      }
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // Mark unread messages from others as read
      if (user && data?.length) {
        const unreadIds = data
          .filter(m => m.sender_id !== user.id && !m.read_at)
          .map(m => m.id);
        if (unreadIds.length > 0) {
          await supabase
            .from('chat_messages')
            .update({ read_at: new Date().toISOString() })
            .in('id', unreadIds);
        }
      }
      
      // Get sender profiles for each message
      const messagesWithProfiles = [];
      for (const message of data || []) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('user_id', message.sender_id)
          .single();
        
        messagesWithProfiles.push({
          ...message,
          sender: {
            full_name: profileData?.full_name || 'Usuario',
            avatar_url: profileData?.avatar_url
          }
        });
      }
      
      setMessages(messagesWithProfiles);
      hasFetchedRef.current = true;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (message: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('chat_messages')
        .insert({
          chat_id: chatId,
          sender_id: user.id,
          message
        });

      if (error) throw error;
      
      // Update last message in chat
      await supabase
        .from('chats')
        .update({ 
          last_message: message,
          last_message_at: new Date().toISOString()
        })
        .eq('id', chatId);

    } catch (err: any) {
      console.error('Error sending message:', err.message);
      throw err;
    }
  };

  useEffect(() => {
    fetchMessages();

    // Set up real-time subscription for messages
    const channel = supabase
      .channel(`chat-${chatId}-messages`)
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `chat_id=eq.${chatId}` }, 
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId]);

  return { 
    messages, 
    loading, 
    error, 
    sendMessage,
    refetch: fetchMessages 
  };
}

// Helper function to create or get a chat between two users
export async function createOrGetChat(otherUserId: string): Promise<string> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, try to find existing chat
    const { data: existingChat } = await supabase
      .from('chats')
      .select('id')
      .or(`and(user1_id.eq.${user.id},user2_id.eq.${otherUserId}),and(user1_id.eq.${otherUserId},user2_id.eq.${user.id})`)
      .single();

    if (existingChat) {
      return existingChat.id;
    }

    // Create new chat if none exists
    const { data: newChat, error } = await supabase
      .from('chats')
      .insert({
        user1_id: user.id,
        user2_id: otherUserId
      })
      .select('id')
      .single();

    if (error) throw error;
    return newChat.id;
  } catch (error) {
    console.error('Error creating/getting chat:', error);
    throw error;
  }
}